/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { ReactNode, useEffect } from 'react';

// React query
import { queryClient } from '@lib/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Mantine
import {
	ColorSchemeScript,
	MantineProvider,
} from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { Notifications } from '@mantine/notifications';

// All global styles separately
import '@mantine/core/styles/baseline.css';
import '@mantine/core/styles/default-css-variables.css';
import '@mantine/core/styles/global.css';

import '@mantine/core/styles.css';
import '@mantine/core/styles.layer.css';

// Style dates
import '@mantine/dates/styles.css';

// Style datatable
import 'mantine-datatable/styles.layer.css';

// Style notification
import '@mantine/notifications/styles.css';

// Style dropzone
import '@mantine/dropzone/styles.css';

// Style charts
import '@mantine/charts/styles.css';

// Style carrousel
import '@mantine/carousel/styles.css';

// i18n
import '@config/i18n/config';
import { useTranslation } from 'react-i18next';

// Dayjs
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
dayjs.extend(customParseFormat);
dayjs.locale('fr');
dayjs.locale('en');
dayjs.extend(utc);

// Hooks
import { useLocalStorage } from '@hooks/useLocalStorage';

// UI lib components

import { light } from 'shared/themes';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */
type AppProviderProps = {
	children: ReactNode;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const AppProvider = ({
	children,
}: AppProviderProps) => {
	/* ******************************** HOOKS ******************************* */
	const { i18n } = useTranslation();
	const [lng] = useLocalStorage('lng', 'fr');

	useEffect(() => {
		if (lng) i18n.changeLanguage(lng);
	}, [i18n, lng]);

	/* ******************************** RENDERING ******************************* */
	return (
		<MantineProvider
			defaultColorScheme="light"
			theme={light}
		>
			<ColorSchemeScript />
			<DatesProvider
				settings={{
					locale: i18n.language ? i18n.language : 'en',
				}}
			>
				<QueryClientProvider client={queryClient}>
					<ReactQueryDevtools initialIsOpen={false} />
					<Notifications />
					{children}
				</QueryClientProvider>
			</DatesProvider>
		</MantineProvider>
	);
};
